import { Separator } from "app/shadcnComponents/ui/separator";
import Button from "app/storybookComponents/Button";
import { useState } from "react";
import { getS } from "utils/helperFunctions";
import UserSelector from "./UserSelector";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";
import { ChangesSummary, DeactivateUserAutomaticTeamSetup } from "./types";
import Checkbox from "../Checklists/Checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { cn } from "app/lib/utils";
import SelectedUserBox from "./SelectedUserBox";

interface Props {
  managerUserAccountId?: number | null;
  directReportsUserAccountIds: number[];
  onStepForward: () => void;
  onStepBack: () => void;
  onUpdateChangesSummary?: (changesSummary: ChangesSummary) => void;
  initialAutomaticTeamSetup?: DeactivateUserAutomaticTeamSetup;
}

// This step will be used to
const ReassignDirectReportsStep = ({
  directReportsUserAccountIds,
  onStepForward,
  onStepBack,
  managerUserAccountId,
  onUpdateChangesSummary,
  initialAutomaticTeamSetup,
}: Readonly<Props>) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const [newManagerUserAccountId, setNewManagerUserAccountId] = useState<
    number | null
  >(initialAutomaticTeamSetup?.newManagerUserAccountId ?? null);
  const [unassignManagerForAllUsers, setUnassignManagerForAllUsers] = useState(
    initialAutomaticTeamSetup?.unassignManagerForAllUsers ?? false
  );
  const [error, setError] = useState<string | null>(null);

  const managerName = (() => {
    if (!managerUserAccountId) return "This person";
    const user = usersById[managerUserAccountId];
    const name = `${user?.firstName} ${user?.lastName}`.trim();
    return name || "This person";
  })();

  const clearError = () => {
    setError(null);
  };

  const getNewManagerSelector = () => {
    const excludedUserAccountIds = [...directReportsUserAccountIds];
    if (managerUserAccountId) {
      excludedUserAccountIds.push(managerUserAccountId);
    }

    return (
      <UserSelector
        onUserSelected={(userAccountId) => {
          setNewManagerUserAccountId(userAccountId);
          clearError();
        }}
        placeholderText="Select a new manager"
        excludeUserAccountIds={excludedUserAccountIds}
        isDisabled={unassignManagerForAllUsers}
      />
    );
  };

  const getSelectedManagerElement = () => {
    if (!newManagerUserAccountId || unassignManagerForAllUsers) return null;
    return (
      <div className="my-[8px]">
        <SelectedUserBox
          userAccountId={newManagerUserAccountId}
          onRemoveClick={() => {
            setNewManagerUserAccountId(null);
            clearError();
          }}
        />
      </div>
    );
  };

  const onBackClick = () => {
    onStepBack();
  };

  const onContinueClick = async () => {
    if (!newManagerUserAccountId && !unassignManagerForAllUsers) {
      setError(
        "*You need to select a new manager or unassign manager for all users"
      );

      return;
    }

    onUpdateChangesSummary?.({
      automaticTeamChanges: {
        newManagerUserAccountId: unassignManagerForAllUsers
          ? null
          : newManagerUserAccountId,
        unassignManagerForAllUsers,
      },
    });
    onStepForward();
  };

  return (
    <>
      <p>
        <b>
          {managerName} is manager of {directReportsUserAccountIds.length} user
          {getS(directReportsUserAccountIds.length)}.
        </b>{" "}
        What should happen to
        {directReportsUserAccountIds.length === 1 ? " this" : " these"} user
        {getS(directReportsUserAccountIds.length)}?
      </p>
      <div
        className={cn(
          "flex flex-col gap-2 bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200",
          error && "border-red-500 border-[2px]"
        )}
      >
        {error && <p className="text-red-500">{error}</p>}
        {getNewManagerSelector()}
        {getSelectedManagerElement()}
        <div className="unassign-manager-checkbox-container">
          <Checkbox
            id="unassign-manager-for-all-users"
            label={
              directReportsUserAccountIds.length === 1
                ? "Unassign manager for this user"
                : "Unassign manager for all users"
            }
            value={unassignManagerForAllUsers}
            onChange={(newValue) => {
              setUnassignManagerForAllUsers(newValue);
              clearError();
            }}
          />
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-yellow-500"
            />
            <p>
              {directReportsUserAccountIds.length === 1
                ? "This person"
                : `These ${directReportsUserAccountIds.length} people`}{" "}
              will not be able to do check-ins until assigned a new manager
            </p>
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex justify-end gap-2">
        <Button onClick={onBackClick} variant="secondary-gray">
          Back
        </Button>
        <Button onClick={onContinueClick}>Continue</Button>
      </div>
    </>
  );
};

export default ReassignDirectReportsStep;
